// Frontend-owned propagation. Janus consumes the resulting mask; it does not decide obstacle widths.
import { wallRects, boxIsRoofed } from './ft3d-shape-core.js';
import { stairSightContour } from './ft3d-sight-core.js';
const EPS = 0.001;
const mix = (a,b,t) => ({x:a.x+(b.x-a.x)*t,y:a.y+(b.y-a.y)*t,z:a.z+(b.z-a.z)*t});
const center = b => ({x:(b.min.x+b.max.x)/2,y:(b.min.y+b.max.y)/2,z:(b.min.z+b.max.z)/2});
const inside = (p,b) => ['x','y','z'].every(k=>p[k]>=b.min[k]-EPS&&p[k]<=b.max[k]+EPS);
export function aoeSectionHull(points) {
  const sorted=[...points].sort((a,b)=>a.x-b.x||a.y-b.y);
  const cross=(a,b,c)=>(b.x-a.x)*(c.y-a.y)-(b.y-a.y)*(c.x-a.x);
  const half=list=>{const out=[];for(const p of list){while(out.length>1&&cross(out.at(-2),out.at(-1),p)<=0)out.pop();out.push(p);}out.pop();return out;};
  return [...half(sorted),...half([...sorted].reverse())];
}
function inPolygon(p, polygon) {
  let yes=false;
  for(let i=0,j=polygon.length-1;i<polygon.length;j=i++) {
    const a=polygon[i],b=polygon[j];
    if((a.y>p.y)!==(b.y>p.y)&&p.x<(b.x-a.x)*(p.y-a.y)/(b.y-a.y)+a.x) yes=!yes;
  }
  return yes;
}
export function aoeBarrierHit(source, target, barrier) {
  let lo=0,hi=1;
  for(const k of ['x','y','z']) {
    const d=target[k]-source[k];
    if(Math.abs(d)<1e-9) { if(source[k]<barrier.min[k]-EPS||source[k]>barrier.max[k]+EPS)return null; }
    else { const a=(barrier.min[k]-source[k])/d,b=(barrier.max[k]-source[k])/d;lo=Math.max(lo,Math.min(a,b));hi=Math.min(hi,Math.max(a,b)); }
  }
  const pad=EPS/Math.max(EPS,Math.hypot(target.x-source.x,target.y-source.y,target.z-source.z));
  if(lo>hi+1e-9||lo<=pad||lo>=1-pad)return null;
  const point=mix(source,target,lo);
  if(barrier.holes?.some(polygon=>inPolygon(point,polygon)))return null;
  return lo;
}

function subtractBox(rect, hole) {
  const axis=rect.axis, span=axis==='x'?'y':'x';
  if(rect.min[axis]<=hole[axis].min+EPS||rect.min[axis]>=hole[axis].max-EPS)return [rect];
  const a=Math.max(rect.min[span],hole[span].min),b=Math.min(rect.max[span],hole[span].max);
  const c=Math.max(rect.min.z,hole.floor),d=Math.min(rect.max.z,hole.ceiling);
  if(a>=b||c>=d)return [rect];
  const out=[];
  const piece=(low,high,zLow,zHigh)=>{if(high-low>EPS&&zHigh-zLow>EPS)out.push({...rect,min:{...rect.min,[span]:low,z:zLow},max:{...rect.max,[span]:high,z:zHigh}});};
  piece(rect.min[span],a,rect.min.z,rect.max.z);piece(b,rect.max[span],rect.min.z,rect.max.z);
  piece(a,b,rect.min.z,c);piece(a,b,d,rect.max.z);
  return out;
}

export function aoeStructuralBarriers(shape) {
  if(!shape)return [];
  // Closed opaque doors/windows remain solid. Transparent openings admit physical effects only if passable.
  const solidShape={...shape,openings:(shape.openings??[]).filter(o=>o.passable!==false&&o.coveringState?.blocksSight!==true)};
  let walls=wallRects(solidShape).map(r=>({axis:r.axis,
    widthBounds:(()=>{const b=shape.boxes.find(b=>b.id===r.boxId);return r.axis==='x'?{min:{x:r.at,y:b.y.min,z:r.zMin},max:{x:r.at,y:b.y.max,z:r.zMax}}:{min:{x:b.x.min,y:r.at,z:r.zMin},max:{x:b.x.max,y:r.at,z:r.zMax}};})(),
    min:r.axis==='x'?{x:r.at,y:r.spanMin,z:r.zMin}:{x:r.spanMin,y:r.at,z:r.zMin},
    max:r.axis==='x'?{x:r.at,y:r.spanMax,z:r.zMax}:{x:r.spanMax,y:r.at,z:r.zMax},kind:'wall'}));
  walls=walls.flatMap(w=>(shape.boxes??[]).filter(b=>!b.navigationOpenGround).reduce((pieces,b)=>pieces.flatMap(p=>subtractBox(p,b)),[w]));
  // Merge touching coplanar segments before measuring width. Inventory segmentation cannot open a wall.
  walls.sort((a,b)=>a.axis.localeCompare(b.axis)||a.min[a.axis]-b.min[b.axis]||a.min.z-b.min.z||a.max.z-b.max.z||a.min[a.axis==='x'?'y':'x']-b.min[b.axis==='x'?'y':'x']);
  const merged=[];
  for(const b of walls) {
    const a=merged.at(-1),s=b.axis==='x'?'y':'x';
    if(a&&a.axis===b.axis&&Math.abs(a.min[a.axis]-b.min[b.axis])<EPS&&Math.abs(a.min.z-b.min.z)<EPS&&Math.abs(a.max.z-b.max.z)<EPS&&b.min[s]<=a.max[s]+EPS) {
      a.max[s]=Math.max(a.max[s],b.max[s]);
      a.widthBounds.min[s]=Math.min(a.widthBounds.min[s],b.widthBounds.min[s]);
      a.widthBounds.max[s]=Math.max(a.widthBounds.max[s],b.widthBounds.max[s]);
    }
    else merged.push(b);
  }
  const floors=[];
  for(const box of shape.boxes??[]) {
    if(box.navigationOpenGround)continue;
    const elevations=[box.floor];
    if(boxIsRoofed(box))elevations.push(box.creation?.physicalCeilingFt??box.ceiling);
    for(const [i,z] of elevations.entries()) {
      if(i>0&&(shape.boxes??[]).some(b=>b!==box&&Math.abs(b.floor-z)<EPS&&b.x.min<=box.x.min&&b.x.max>=box.x.max&&b.y.min<=box.y.min&&b.y.max>=box.y.max))continue;
      const holes=i===0?(shape.authoring?.verticalConnections??[]).filter(c=>c.targetRoomId===box.id).map(c=>stairSightContour(c,box)).filter(p=>p.length>=3):[];
      // Overlapping authored volumes are open interior space, matching physical sight geometry.
      for(const other of shape.boxes??[])if(other!==box&&!other.navigationOpenGround&&z>other.floor+EPS&&z<other.ceiling-EPS)
        holes.push([{x:other.x.min,y:other.y.min},{x:other.x.max,y:other.y.min},{x:other.x.max,y:other.y.max},{x:other.x.min,y:other.y.max}]);
      floors.push({min:{x:box.x.min,y:box.y.min,z},max:{x:box.x.max,y:box.y.max,z},kind:'floor',holes});
    }
  }
  return [...merged,...floors];
}

/** Height of the effect's axis where it passes over ground point (x,y). A line/cone rises from its CTR to a
 * lifted D ball, so a prop beside the caster meets it low and a prop far along meets it high; every other
 * shape answers its centre. Props are sliced here — slicing at a pitched bolt's midpoint put the cut through
 * the canopies of trees at the caster's feet and turned them into blockers. */
export function aoeAxisHeightAt(intent, endpoints, x, y) {
  if(intent.shape!=='line'&&intent.shape!=='cone')return endpoints.center.z;
  const o=endpoints.origin,e=endpoints.endpoint,dx=e.x-o.x,dy=e.y-o.y,lengthSq=dx*dx+dy*dy;
  const t=lengthSq>EPS?Math.max(0,Math.min(1,((x-o.x)*dx+(y-o.y)*dy)/lengthSq)):0;
  return o.z+(e.z-o.z)*t;
}

/** All coordinates are persisted-world feet (z up). Props are solid collision sections, never creature bodies. */
export function compileAoePropagation(structure, props, intent, endpoints) {
  const directional=intent.shape==='line'||intent.shape==='cone';
  const source={...(directional?endpoints.origin:endpoints.center)};
  // Flat areas propagate just above their contact surface; a sphere retains its actual center.
  if(!directional)source.z+=Math.min(.01,intent.heightFt/2);
  const delta={x:endpoints.endpoint.x-endpoints.origin.x,y:endpoints.endpoint.y-endpoints.origin.y,z:endpoints.endpoint.z-endpoints.origin.z};
  const length=Math.hypot(delta.x,delta.y,delta.z);
  const direction=length>EPS?{x:delta.x/length,y:delta.y/length,z:delta.z/length}:{x:1,y:0,z:0};
  const barriers=[];
  // Parallel line rays stay inside the line's swept box. Distant room faces cannot
  // affect the result and must not grow/recompile the preview's fragment shader.
  // A pitched line keeps the filter (a lifted D ball once dropped it, admitting every wall, floor and
  // canopy within reach): its start-plane rays lean back by up to height·pitch horizontally, and the
  // target mask's samples reach down/up by the rise, so both pads grow with the pitch and vanish when flat.
  const rise=Math.abs(delta.z);
  const linePad=intent.widthFt/2+(length>EPS?intent.heightFt*rise/length:0)+EPS;
  const lineBounds=intent.shape==='line'?{
    min:{x:Math.min(endpoints.origin.x,endpoints.endpoint.x)-linePad,y:Math.min(endpoints.origin.y,endpoints.endpoint.y)-linePad,z:Math.min(endpoints.origin.z,endpoints.endpoint.z)-rise-intent.heightFt/2-EPS},
    max:{x:Math.max(endpoints.origin.x,endpoints.endpoint.x)+linePad,y:Math.max(endpoints.origin.y,endpoints.endpoint.y)+linePad,z:Math.max(endpoints.origin.z,endpoints.endpoint.z)+intent.heightFt+rise+EPS},
  }:null;
  for(const raw of [...structure,...props]) {
    if(raw.kind==='creature')continue;
    if(lineBounds&&['x','y','z'].some(k=>raw.max[k]<lineBounds.min[k]||raw.min[k]>lineBounds.max[k]))continue;
    const b={...raw,min:{...raw.min},max:{...raw.max}},widthBounds=raw.widthBounds??b,c=center(widthBounds);
    const reach=intent.distanceFt*(intent.shape==='cube'||intent.shape==='square'?1:2)+intent.heightFt;
    const nearest={x:Math.max(b.min.x,Math.min(b.max.x,source.x)),y:Math.max(b.min.y,Math.min(b.max.y,source.y)),z:Math.max(b.min.z,Math.min(b.max.z,source.z))};
    if(Math.hypot(nearest.x-source.x,nearest.y-source.y,nearest.z-source.z)>reach)continue;
    const dx=directional?direction.x:c.x-source.x,dy=directional?direction.y:c.y-source.y,h=Math.hypot(dx,dy);
    let width=h>EPS?(Math.abs(dy)*(widthBounds.max.x-widthBounds.min.x)+Math.abs(dx)*(widthBounds.max.y-widthBounds.min.y))/h:Math.max(widthBounds.max.x-widthBounds.min.x,widthBounds.max.y-widthBounds.min.y);
    if(h>EPS&&raw.section?.length) {const projected=raw.section.map(p=>(-dy*p.x+dx*p.y)/h);width=Math.max(...projected)-Math.min(...projected);}
    if(raw.diameterFt>0)width=raw.diameterFt;
    const forward=(c.x-source.x)*direction.x+(c.y-source.y)*direction.y+(c.z-source.z)*direction.z;
    const effectWidth=intent.shape==='line'?intent.widthFt:intent.shape==='cone'?Math.max(EPS,2*forward):
      intent.shape==='cube'||intent.shape==='square'?intent.distanceFt:2*intent.distanceFt;
    // Room faces belong to a connected enclosing shell, not independent freestanding props. A tiny
    // room cannot become permeable just because its authored wall panels are shorter than the blast.
    // Floors are likewise physical partitions in the vertical direction.
    if(b.kind!=='floor'&&b.kind!=='wall'&&width+EPS<effectWidth)continue;
    if(inside(source,b)&&b.kind!=='floor')continue;
    delete b.widthBounds;delete b.axis;delete b.section;delete b.diameterFt;
    barriers.push(b);
  }
  const lineHalfZ=intent.heightFt/2*Math.hypot(direction.x,direction.y);
  const minZ=intent.shape==='sphere'?endpoints.center.z-intent.distanceFt:Math.min(endpoints.origin.z,endpoints.endpoint.z,endpoints.center.z)-(intent.shape==='line'?lineHalfZ:0);
  const maxZ=intent.shape==='sphere'?endpoints.center.z+intent.distanceFt:Math.max(endpoints.origin.z,endpoints.endpoint.z,endpoints.center.z)+(intent.shape==='line'?lineHalfZ:intent.heightFt);
  return {version:1,source,direction,directional,cone:intent.shape==='cone',minZ,maxZ,barriers,
    region:{shape:intent.shape,distanceFt:intent.distanceFt,widthFt:intent.widthFt,heightFt:intent.heightFt,rotationDeg:intent.rotationDeg??0,...endpoints}};
}

export function aoePropagationSource(mask, point) {
  if(!mask.directional)return mask.source;
  // A line/cone advances from its front, not from the segment midpoint.
  if(mask.cone)return mask.source;
  const d=mask.direction,t=(point.x-mask.source.x)*d.x+(point.y-mask.source.y)*d.y+(point.z-mask.source.z)*d.z;
  return {x:point.x-d.x*t,y:point.y-d.y*t,z:point.z-d.z*t};
}
export function aoePropagationReaches(mask, point, entityId='') {
  const source=aoePropagationSource(mask,point);
  return !mask.barriers.some(b=>b.entityId!==entityId&&aoeBarrierHit(source,point,b)!==null);
}
export function aoePropagationVolumeReaches(mask, volume, entityId='') {
  // A partial exposure is a hit; sample the target's near surface and vertical extent, not only its center.
  const low=Math.max(volume.zMin,mask.minZ),high=Math.min(volume.zMax,mask.maxZ);
  if(low>high)return false;
  const z=Math.max(low,Math.min(high,mask.directional?aoeAxisHeightAt(mask.region,mask.region,volume.x,volume.y):mask.source.z));
  const d=mask.direction,along=(volume.x-mask.source.x)*d.x+(volume.y-mask.source.y)*d.y;
  const nearest=mask.directional?{x:mask.source.x+d.x*along,y:mask.source.y+d.y*along}:mask.source;
  const dx=nearest.x-volume.x,dy=nearest.y-volume.y,length=Math.hypot(dx,dy),r=Math.min(volume.r,length);
  const offsets=[[0,0],[volume.r,0],[-volume.r,0],[0,volume.r],[0,-volume.r],[length>EPS?dx/length*r:0,length>EPS?dy/length*r:0]];
  return [z,(low+high)/2,Math.max(low,high-EPS)].some(height=>offsets.some(([x,y])=>{
    const p={x:volume.x+x,y:volume.y+y,z:height};
    return aoePointInRegion(mask,p)&&aoePropagationReaches(mask,p,entityId);
  }));
}

export function aoePointInRegion(mask,p) {
  const r=mask.region;if(!r)return true;
  const dx=p.x-r.center.x,dy=p.y-r.center.y,dz=p.z-r.center.z;
  if(r.shape==='sphere')return Math.hypot(dx,dy,dz)<=r.distanceFt+EPS;
  if(p.z<mask.minZ-EPS||p.z>mask.maxZ+EPS)return false;
  if(r.shape==='circle'||r.shape==='cylinder')return Math.hypot(dx,dy)<=r.distanceFt+EPS;
  if(r.shape==='cube'||r.shape==='square') {
    const a=-r.rotationDeg*Math.PI/180;
    return Math.abs(dx*Math.cos(a)-dy*Math.sin(a))<=r.distanceFt/2+EPS&&Math.abs(dx*Math.sin(a)+dy*Math.cos(a))<=r.distanceFt/2+EPS;
  }
  const d=mask.direction,x=p.x-r.origin.x,y=p.y-r.origin.y,z=p.z-r.origin.z;
  const t=x*d.x+y*d.y+z*d.z;
  if(t<-EPS||t>r.distanceFt+EPS)return false;
  if(r.shape==='cone')return Math.hypot(x,y,z)<=r.distanceFt+EPS&&t>=Math.hypot(x,y,z)*Math.SQRT1_2-EPS;
  const h=Math.hypot(d.x,d.y);
  const side=h>EPS?(-d.y*x+d.x*y)/h:y;
  const up=h>EPS?-(d.x*x+d.y*y)*d.z/h+z*h:x;
  return Math.abs(side)<=r.widthFt/2+EPS&&Math.abs(up)<=r.heightFt/2+EPS;
}
